import React, { useEffect, useCallback, useState, type FC } from "react";
import "~/css/reception/ReceptionHeader.css";
import { useAtom } from "jotai";
import { currentVRMAtom, isSettingOpenAtom, languageAtom, T2SEngineAtom, volumeAtom } from "~/Atom";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import { getAppSettingsData, type AppSettingsResponse } from "~/utils/receptionDB";
import { getVRMName } from "~/vrm_utils/VRMUtils";
import * as db from "~/components/db/CharacterDBUtils";
import { PlaySound } from "~/SoundUtils";
import { ROUTE } from "~/constants/routes";
import * as icon from "~/Icons";
import HamburgerMenu from "~/components/settings/HamburgerMenu";

const SUPPORTED_LANGUAGE_MAP: Record<string, { label: string; code: string }> = {
  ja: { label: "日本語", code: "ja-JP" },
  en: { label: "English", code: "en-US" },
  vi: { label: "Tiếng Việt", code: "vi-VN" },
};

/**
 * 受付画面の共通ヘッダーコンポーネント（Figma Header準拠）。
 * 言語切替・受付中止・設定ボタンを水平に配置する。
 */
type Props = {
  showBackButton?: boolean;
  onBackButtonClick?: () => void;
  backButtonLabel?: string;
  backButtonTranslationKey?: string;
};

const ReceptionHeader: FC<Props> = ({
  showBackButton = false,
  onBackButtonClick,
  backButtonLabel,
  backButtonTranslationKey,
  // eslint-disable-next-line complexity
}) => {
  const [language, setLanguage] = useAtom(languageAtom);
  const [currentVRM] = useAtom(currentVRMAtom);
  const [, setIsSettingOpen] = useAtom(isSettingOpenAtom);
  const [, setT2SEngine] = useAtom(T2SEngineAtom);
  const [volume] = useAtom(volumeAtom);
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [availableLanguages, setAvailableLanguages] = useState<
    Array<{ label: string; code: string }>
  >([]);
  const resolvedBackButtonLabel = backButtonLabel ?? t(backButtonTranslationKey ?? "buttonBack");
  const changeLanguage = useCallback(
    (languageCode: string) => i18n.changeLanguage(languageCode),
    [i18n]
  );

  const showCancelButton =
    location.pathname !== ROUTE.RECEPTION.LOADING && location.pathname !== ROUTE.RECEPTION.COMPLETE;
  const showLanguageButton =
    location.pathname === ROUTE.RECEPTION.LOADING ||
    location.pathname === ROUTE.RECEPTION.COMPANY ||
    location.pathname === ROUTE.RECEPTION.MENU;
  const updateT2SEngine = useCallback(
    async (selectedLanguage: string) => {
      try {
        if (!currentVRM) return;
        const currentCharacter = getVRMName(currentVRM);
        const characterData = await db.selectByName(currentCharacter);
        const defaultEngine = selectedLanguage === "ja-JP" ? "VOICEVOX" : "OpenAI";
        if (!characterData || !characterData.tts_engine) {
          setT2SEngine(defaultEngine);
          return;
        }
        setT2SEngine(selectedLanguage === "ja-JP" ? characterData.tts_engine : "OpenAI");
      } catch (error) {
        setT2SEngine(selectedLanguage === "ja-JP" ? "VOICEVOX" : "OpenAI");
        console.error("Error updating T2S engine:", error);
      }
    },
    [currentVRM, setT2SEngine]
  );

  useEffect(() => {
    void updateT2SEngine(language);
  }, [language, updateT2SEngine]);

  const extractLanguageCodes = useCallback((parsed: AppSettingsResponse): string[] => {
    if (!parsed) return [];
    const appSettingSource = parsed?.data?.list?.[0]?.appSetting;
    const words = appSettingSource?.words;
    if (!Array.isArray(words) || words.length === 0) return [];
    return words.map((w) => w.languageCode);
  }, []);

  useEffect(() => {
    const applyLanguageCodes = (codes: string[]) => {
      try {
        const mapped = codes
          .map((c) => SUPPORTED_LANGUAGE_MAP[c])
          .filter((v): v is { label: string; code: string } => Boolean(v));
        if (mapped.length === 0) {
          setAvailableLanguages([]);
          setLanguage("ja-JP");
          void changeLanguage("ja-JP");
          return;
        }
        setAvailableLanguages(mapped);
        if (!mapped.find((m) => m.code === language)) {
          const newCode = mapped[0].code;
          setLanguage(newCode);
          void changeLanguage(newCode);
        }
      } catch (error) {
        setAvailableLanguages([]);
        setLanguage("ja-JP");
        void changeLanguage("ja-JP");
        console.error("Failed to load appSetting languages:", error);
      }
    };

    const loadFromIndexedDb = async () => {
      try {
        const stored = await getAppSettingsData();
        const codes = stored?.receptionCommonWord?.items?.map((item) => item.languageCode) ?? [];
        applyLanguageCodes(codes);
      } catch (e) {
        console.error("Failed to load appSetting from IndexedDB:", e);
      }
    };

    void loadFromIndexedDb();

    const onChange = (e: Event) => {
      try {
        const settings = (e as CustomEvent<AppSettingsResponse>).detail;
        const codes = extractLanguageCodes(settings);
        applyLanguageCodes(codes);
      } catch {
        // ignore
      }
    };

    window.addEventListener("reception:appSettings:changed", onChange as EventListener);
    return () => {
      window.removeEventListener("reception:appSettings:changed", onChange as EventListener);
    };
  }, [changeLanguage, extractLanguageCodes, language, setLanguage]);

  const handleLanguageChange = useCallback(
    async (e: React.ChangeEvent<HTMLSelectElement>) => {
      const selectedLanguage = e.target.value;
      if (selectedLanguage === language) {
        PlaySound("push", volume);
        return;
      }
      setLanguage(selectedLanguage);
      try {
        await changeLanguage(selectedLanguage);
      } catch (error) {
        console.error("Error changing language:", error);
      }
      PlaySound("push", volume);
    },
    [changeLanguage, language, setLanguage, volume]
  );

  const handleSettingsToggle = useCallback(() => {
    setIsSettingOpen((value) => !value);
    PlaySound("push", volume);
  }, [setIsSettingOpen, volume]);

  const handleSettingsPointerDown = useCallback(
    (event: React.PointerEvent<HTMLButtonElement>) => {
      event.preventDefault();
      if (event.pointerType === "mouse") {
        handleSettingsToggle();
      }
    },
    [handleSettingsToggle]
  );

  const handleCancelReception = useCallback(() => {
    PlaySound("push", volume);
    navigate(ROUTE.RECEPTION.LOADING, {
      state: { skipStartupModal: true },
    });
  }, [navigate, volume]);

  const handleBackButtonClick = useCallback(() => {
    PlaySound("push", volume);
    onBackButtonClick?.();
  }, [onBackButtonClick, volume]);

  return (
    <>
      <svg width="0" height="0" style={{ position: "absolute" }} aria-hidden="true">
        <defs>
          <linearGradient id="header-icon-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#1E6EFF" />
            <stop offset="100%" stopColor="#9CE7F4" />
          </linearGradient>
        </defs>
      </svg>
      <header className="reception-header">
        <div className="reception-header__leading">
          {showBackButton && (
            <button
              type="button"
              onClick={handleBackButtonClick}
              className="reception-header__icon-btn reception-header__back-btn"
              aria-label={resolvedBackButtonLabel}
              data-reception-interactive="true"
            >
              <icon.icon_back aria-hidden="true" />
            </button>
          )}
        </div>
        <div className="reception-header__actions">
          {showCancelButton && (
            <button
              type="button"
              className="reception-header__text-btn"
              data-reception-interactive="true"
              onClick={handleCancelReception}
              aria-label={t("receptionSidebar.cancelReception")}
            >
              <icon.icon_close />
              <span>{t("receptionSidebar.cancelReception")}</span>
            </button>
          )}

          {availableLanguages.length > 0 && showLanguageButton && (
            <div
              className="reception-header__icon-btn reception-header__lang-btn"
              data-reception-interactive="true"
            >
              <icon.icon_language />
              <select
                className="reception-header__lang-select"
                value={language}
                onChange={handleLanguageChange}
                aria-label="language-select"
              >
                {availableLanguages.map(({ code, label }) => (
                  <option key={code} value={code}>
                    {label}
                  </option>
                ))}
              </select>
            </div>
          )}

          <button
            type="button"
            className="reception-header__icon-btn"
            data-reception-interactive="true"
            onPointerDown={handleSettingsPointerDown}
            aria-label="settings"
          >
            <icon.icon_settings />
          </button>
        </div>
      </header>

      <HamburgerMenu />
    </>
  );
};

export default ReceptionHeader;
